import { useState } from "react";
import { Tooltip, Rate, Modal } from "antd";
import { FileTextOutlined, EyeOutlined } from "@ant-design/icons";
import { fetchNoticeReplies } from "../../../services/customer360Service";
import {
  cardStyle, num, dateTime, statusChip, chip, TONE, titleCase,
  PanelHeader, PanelTable, PanelSkeleton, PanelError, usePanelData,
} from "./shared";

/**
 * Notice replies drafted for this account, and how far each one got.
 *
 * The header pairs two counts on purpose: how many replies exist against how
 * many were actually generated. A gap between them is the interesting thing —
 * it is work that was started and never produced anything.
 */
/**
 * Undo HTML escaping, when there is any.
 *
 * The reply is stored escaped — `&lt;p&gt;` rather than `<p>`. An `srcDoc`
 * written through the DOM property is never entity-decoded by the parser, so
 * that string reaches the iframe intact and renders as the tags themselves,
 * printed as text.
 *
 * Only applied when the value actually looks escaped: running it over real HTML
 * would turn an `&amp;` the author typed into a bare ampersand.
 */
const decodeHtml = (raw) => {
  const s = String(raw || "");
  if (!s.includes("&lt;") || s.includes("<")) return s;
  const holder = document.createElement("textarea");
  // Content of a textarea is parsed as text, never executed.
  holder.innerHTML = s;
  return holder.value;
};

/* The reply arrives as a fragment with no document around it, so an iframe
   renders it in the browser's default serif at the very edge of the frame.
   These are the minimum styles that make it read as the letter it is. */
const PREVIEW_CSS = `
  body { margin: 0; padding: 28px 32px; background: #fff;
         font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
         font-size: 13.5px; line-height: 1.75; color: #1e293b; }
  h1,h2,h3,h4 { color: #0f172a; margin: 22px 0 10px; line-height: 1.35; }
  h1 { font-size: 20px } h2 { font-size: 17px } h3 { font-size: 15px } h4 { font-size: 14px }
  p { margin: 0 0 11px }
  img { max-width: 100%; height: auto }
  blockquote { margin: 14px 0; padding: 10px 16px; border-left: 3px solid #c7d2fe;
               background: #f8fafc; color: #475569; }
  table { border-collapse: collapse; width: 100%; margin: 14px 0 }
  th, td { border: 1px solid #e2e8f0; padding: 7px 10px; text-align: left; font-size: 13px }
  th { background: #f8fafc; font-weight: 700 }
  ol, ul { margin: 0 0 11px; padding-left: 22px }
  hr { border: none; border-top: 1px solid #e2e8f0; margin: 20px 0 }
  a { color: #4f46e5 }
`;

/**
 * The drafted reply, shown as it was written.
 *
 * Rendered inside a sandboxed iframe rather than injected into this page. The
 * HTML comes from an editor a customer typed into, so it is untrusted content:
 * an empty `sandbox` blocks scripts, forms, navigation and any access to this
 * origin, which `dangerouslySetInnerHTML` would not — inline handlers like
 * `onerror` still fire there even though `<script>` tags do not.
 */
const ReplyPreview = ({ row, onClose }) => {
  const html = decodeHtml(row?.editor_html);
  return (
    <Modal
      open={Boolean(row)}
      onCancel={onClose}
      footer={null}
      width={900}
      styles={{ body: { padding: 0 } }}
      title={(
        <div>
          <div style={{ fontWeight: 700, fontSize: 15 }}>Drafted reply</div>
          <div style={{ fontSize: 12, color: "#6b7280", fontWeight: 400, marginTop: 2 }}>
            {[row?.tm_number, titleCase(row?.task_type)].filter(Boolean).join(" · ")}
          </div>
        </div>
      )}
    >
      <iframe
        title="Notice reply preview"
        sandbox=""
        srcDoc={`<!doctype html><html><head><meta charset="utf-8"><style>${PREVIEW_CSS}</style></head><body>${html}</body></html>`}
        style={{
          width: "100%", height: "72vh", border: "1px solid #e2e8f0",
          borderRadius: 10, background: "#fff", display: "block",
        }}
      />
    </Modal>
  );
};

const NoticeRepliesTab = ({ userId, active }) => {
  const p = usePanelData(fetchNoticeReplies, userId, active);
  const [previewing, setPreviewing] = useState(null);

  if (p.loading && !p.data) return <PanelSkeleton />;
  if (p.error) return <PanelError error={p.error} onRetry={p.reload} />;

  const generated = Number(p.data?.generated_count) || 0;

  const columns = [
    {
      title: "TM number", dataIndex: "tm_number", width: 130,
      render: (v) => <span style={{ fontWeight: 700, color: "#0f172a", fontSize: 12.5 }}>{v || "—"}</span>,
    },
    {
      title: "Task", dataIndex: "task_type", width: 160,
      render: (v) => (v ? chip(titleCase(v), TONE.violet) : <span style={{ color: "#cbd5e1" }}>—</span>),
    },
    {
      title: "Opposition", dataIndex: "opposition_number", width: 140,
      render: (v) => <span style={{ fontSize: 12.5, color: "#475569" }}>{v || "—"}</span>,
    },
    { title: "Status", dataIndex: "status", width: 120, render: (v) => statusChip(v) },
    {
      title: "Stage", dataIndex: "stage", width: 150,
      render: (v) => (v ? chip(titleCase(v), TONE.blue) : <span style={{ color: "#cbd5e1" }}>—</span>),
    },
    {
      title: "Format", dataIndex: "format_selected", width: 130,
      render: (v) => <span style={{ fontSize: 12.5, color: "#64748b" }}>{titleCase(v)}</span>,
    },
    {
      title: "Assigned to", dataIndex: "assigned_to_name", width: 160,
      render: (v) => <span style={{ fontSize: 12.5, color: "#334155", fontWeight: 500 }}>{v || "Unassigned"}</span>,
    },
    {
      title: "Review", key: "review", width: 150,
      /* Stars only once there is a rating — an empty five-star row reads as
         "rated zero", which is not the same as "not reviewed". */
      render: (_, r) => {
        const stars = Number(r.review_rating) || 0;
        if (!stars) return <span style={{ color: "#cbd5e1", fontSize: 12 }}>Not reviewed</span>;
        return (
          <Tooltip title={r.reviewed_at ? `Reviewed ${dateTime(r.reviewed_at)}` : undefined}>
            <span><Rate disabled allowHalf value={stars} style={{ fontSize: 12 }} /></span>
          </Tooltip>
        );
      },
    },
    {
      title: "Completed", dataIndex: "completed_at", width: 165,
      render: (v) => (v
        ? <span style={{ fontSize: 12.5, color: "#16a34a", whiteSpace: "nowrap" }}>{dateTime(v)}</span>
        : chip("Open", TONE.amber)),
    },
    {
      title: "Preview", key: "preview", width: 105,
      /* Only where something was actually drafted. A disabled button on every
         other row would imply a reply exists and is merely unavailable. */
      render: (_, r) => (r.editor_html
        ? (
          <button
            onClick={() => setPreviewing(r)}
            style={{
              display: "inline-flex", alignItems: "center", gap: 6, padding: "4px 12px",
              borderRadius: 8, border: "1px solid #ddd6fe", background: "#f5f3ff",
              color: "#6366f1", fontSize: 12, fontWeight: 600, cursor: "pointer",
            }}
          >
            <EyeOutlined /> View
          </button>
        )
        : <span style={{ color: "#cbd5e1" }}>—</span>),
    },
    {
      title: "Created", dataIndex: "created_at", width: 165,
      render: (v) => <span style={{ fontSize: 12.5, color: "#94a3b8", whiteSpace: "nowrap" }}>{dateTime(v)}</span>,
    },
  ];

  return (
    <div style={cardStyle}>
      <PanelHeader
        icon={<FileTextOutlined />}
        title="Notice replies"
        subtitle="Objection and opposition replies raised on this account"
        iconBg="linear-gradient(135deg,#ea580c,#f97316)"
        right={chip(`${num(generated)} generated of ${num(p.total)}`, TONE.amber)}
      />
      <PanelTable
        columns={columns}
        rows={p.rows}
        loading={p.loading}
        page={p.page}
        total={p.total}
        perPage={p.perPage}
        onPage={p.goToPage}
        empty="No notice replies raised on this account yet."
        scrollX={1585}
      />
      <ReplyPreview row={previewing} onClose={() => setPreviewing(null)} />
    </div>
  );
};

export default NoticeRepliesTab;
